import {
  AuditAudioInMatch,
  AuditLiveMention,
  AuditMatch,
  AuditSchedule,
  MinimumAuditMatch,
} from '@mi-front/core';
import { VerifyMatchTarget } from './verify-match-types';

/**
 * Maps a spot match row to a verify-dialog target.
 */
export function getSpotVerifyMatchTargetFromMatch(match: AuditMatch): VerifyMatchTarget {
  return { kind: 'spot', match };
}

/**
 * Maps a live-mention row to a verify-dialog target.
 */
export function getMentionVerifyMatchTargetFromMention(
  mention: AuditLiveMention,
): VerifyMatchTarget {
  return { kind: 'mention', mention };
}

/**
 * Builds a verify-match spot target from a schedule list row that has an embedded match.
 * Schedule `matches` are `MinimumAuditMatch` (no creative); audio lives on the schedule.
 */
export function getSpotVerifyMatchTargetFromSchedule(
  schedule: AuditSchedule,
): VerifyMatchTarget | null {
  const minimumMatch = schedule.matches?.[0];
  if (!minimumMatch) {
    return null;
  }

  return {
    kind: 'spot',
    match: toAuditMatchFromSchedule(schedule, minimumMatch),
  };
}

/**
 * Builds a verify-match mention target from a schedule list row with a linked mention.
 */
export function getMentionVerifyMatchTargetFromSchedule(
  schedule: AuditSchedule,
): VerifyMatchTarget | null {
  if (!schedule.mentionMatch) {
    return null;
  }

  return {
    kind: 'mention',
    mention: schedule.mentionMatch,
  };
}

/**
 * Lifts a schedule-embedded match into a full `AuditMatch` for the verify dialog.
 */
function toAuditMatchFromSchedule(schedule: AuditSchedule, match: MinimumAuditMatch): AuditMatch {
  return {
    type: match.type,
    datetime: match.datetime,
    playedDuration: match.playedDuration,
    score: match.score,
    matchedSchedule: match.matchedSchedule ?? schedule,
    matchedScheduleDiff: match.matchedScheduleDiff,
    matchedScheduleConfirmationType: match.matchedScheduleConfirmationType,
    audio: toAudioInMatch(schedule),
    source: 'PROVIDER',
  };
}

/**
 * Maps schedule creative audio into the shape expected by spot match players.
 */
function toAudioInMatch(schedule: AuditSchedule): AuditAudioInMatch | undefined {
  if (!schedule.audio) {
    return undefined;
  }

  const { id: _id, ...audio } = schedule.audio;
  return audio;
}
